import { create } from "zustand";
import type { StorageAdapter } from "../auth/storage";

export interface TimerState {
  /** ID of the artwork request being timed */
  requestId: string | null;
  /** Epoch ms when the timer was last started/resumed */
  startedAt: number | null;
  /** Previously accumulated milliseconds (from pauses) */
  accumulatedMs: number;
  /** Whether the timer is currently running */
  isRunning: boolean;

  /** Start timing for a request */
  start: (requestId: string) => void;
  /** Pause the timer (preserves accumulated time) */
  pause: () => void;
  /** Resume a paused timer */
  resume: () => void;
  /** Stop the timer and return total elapsed minutes */
  stop: () => number;
  /** Reset the timer completely */
  reset: () => void;
  /** Get current elapsed milliseconds (including running time) */
  getElapsedMs: () => number;
}

let _storageAdapter: StorageAdapter | null = null;
const STORAGE_KEY = "hops-panel-timer";

/** Call this to enable timer persistence across panel close/reopen. */
export function initTimerStorage(adapter: StorageAdapter) {
  _storageAdapter = adapter;
}

function persistTimer(state: {
  requestId: string | null;
  startedAt: number | null;
  accumulatedMs: number;
  isRunning: boolean;
}) {
  _storageAdapter?.setItem(
    STORAGE_KEY,
    JSON.stringify({
      requestId: state.requestId,
      startedAt: state.startedAt,
      accumulatedMs: state.accumulatedMs,
      isRunning: state.isRunning,
    }),
  );
}

export const useTimerStore = create<TimerState>()((set, get) => ({
  requestId: null,
  startedAt: null,
  accumulatedMs: 0,
  isRunning: false,

  start: (requestId: string) => {
    const newState = {
      requestId,
      startedAt: Date.now(),
      accumulatedMs: 0,
      isRunning: true,
    };
    set(newState);
    persistTimer(newState);
  },

  pause: () => {
    const { startedAt, accumulatedMs, isRunning, requestId } = get();
    if (!isRunning || !startedAt) return;
    const elapsed = Date.now() - startedAt;
    const newState = {
      requestId,
      startedAt: null,
      accumulatedMs: accumulatedMs + elapsed,
      isRunning: false,
    };
    set(newState);
    persistTimer(newState);
  },

  resume: () => {
    const { isRunning, requestId, accumulatedMs } = get();
    if (isRunning || !requestId) return;
    const newState = {
      requestId,
      startedAt: Date.now(),
      accumulatedMs,
      isRunning: true,
    };
    set(newState);
    persistTimer(newState);
  },

  stop: () => {
    const totalMs = get().getElapsedMs();
    const totalMinutes = Math.round(totalMs / 60_000);
    set({
      requestId: null,
      startedAt: null,
      accumulatedMs: 0,
      isRunning: false,
    });
    _storageAdapter?.removeItem(STORAGE_KEY);
    return totalMinutes;
  },

  reset: () => {
    set({
      requestId: null,
      startedAt: null,
      accumulatedMs: 0,
      isRunning: false,
    });
    _storageAdapter?.removeItem(STORAGE_KEY);
  },

  getElapsedMs: () => {
    const { startedAt, accumulatedMs, isRunning } = get();
    if (!isRunning || !startedAt) return accumulatedMs;
    return accumulatedMs + (Date.now() - startedAt);
  },
}));

/** Restore persisted timer state from storage. Call on app init. */
export async function restoreTimer(): Promise<boolean> {
  if (!_storageAdapter) return false;
  const raw = await _storageAdapter.getItem(STORAGE_KEY);
  if (!raw) return false;
  try {
    const { requestId, startedAt, accumulatedMs, isRunning } = JSON.parse(raw);
    if (requestId != null) {
      useTimerStore.setState({
        requestId,
        startedAt,
        accumulatedMs,
        isRunning,
      });
      return true;
    }
  } catch {
    // Corrupt data, ignore
  }
  return false;
}
